/**
 *
 * Jira
 *
 */

import React, { memo, useCallback, useEffect, useReducer } from 'react';

import { Clients } from '@ariksa/notification';
import { JiraClientInfo } from '@ariksa/notification/api';
import { Alert, AlertIcon, Box } from '@chakra-ui/react';
import { map, includes, each, find } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { Form, FormAction, FormSchema } from 'components/DataEntry/Form';
import { PageHeaderWithIcon } from 'components/index';
import { JIRAHelp } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Jira/Components/Help/JIRAHelp';
import { useIntegrations } from 'containers/Setup/Integrations/hooks/useIntegrations';
import { selectIntegrations } from 'containers/Setup/Integrations/selectors';
import { actions } from 'containers/Setup/Integrations/slice';

import { formStyles } from '../../../../styles';

interface Props {
  handleSubmit: (data: any) => void;
  actionType?: FormAction;
}

export const JiraForm = memo((props: Props) => {
  const { handleSubmit, actionType = 'Add' } = props;

  const {
    jiraProjects,
    jiraProjectDetails,
    activeClient,
    client,
    clients,
    validateClient,
  } = useSelector(selectIntegrations);
  const { onCancel } = useIntegrations();
  const { data: currentRecord } = activeClient;
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      projects: [],
      users: [],
      issueTypeOptions: [],
      boardOptions: [],
      selectedProject: {},
      selectedBoard: {},
      selectedUser: {},
      issueType: {},
      isEdit: false,
      isHelpOpen: false,
      name: '',
      user: '',
      url: '',
      token: '',
      nameError: '',
      names: [],
      validateProjects: false,
      validateToken: false,
      validateClient: false,
      showValidatedMessage: false,
    },
  );
  const dispatch = useDispatch();

  const toggleIsHelpOpen = () => updateState({ isHelpOpen: !state.isHelpOpen });

  const getJiraProjects = useCallback(
    (user, url, token) => {
      dispatch(
        actions.getJiraProjects({
          q: {
            jiraClientRequest: {
              user,
              url,
              token,
              include_details: false,
            },
          },
          onSuccess: () => updateState({ validateProjects: true }),
          onError: () => updateState({ validateProjects: false }),
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    let names: string[] = [];
    each(clients.jira, o => names.push(o.name));
    updateState({ names });
  }, [clients.jira]);

  //set isEdit true if opened in edit
  useEffect(() => {
    updateState({ isEdit: actionType === 'Update' });
  }, [actionType]);

  //set fields
  useEffect(() => {
    if (state.isEdit)
      updateState({
        name: currentRecord?.name,
        user: currentRecord?.jira?.user,
        url: currentRecord?.jira?.url,
        validateToken: true,
        validateProjects: true,
        validateClient: true,
        showValidatedMessage: true,
        selectedProject: {
          label: currentRecord.jira?.project_name,
          value: currentRecord.jira?.project_id,
        },
        selectedBoard: {
          label: currentRecord?.jira?.default_board_name,
          value: currentRecord?.jira?.default_board_id,
        },
        selectedUser: {
          label: currentRecord.jira?.user,
          value: currentRecord.jira?.account_id,
        },
        issueType: {
          label: currentRecord?.jira?.default_issue_type,
          value: currentRecord?.jira?.default_issue_type,
        },
      });
  }, [currentRecord, state.isEdit]);

  //set list of projects and users
  useEffect(() => {
    if (!state.isEdit) {
      const clientInfoData = jiraProjects.data;
      const projects = map(clientInfoData?.projects, o => ({
        label: o.name,
        value: o.id,
        data: o,
      }));
      updateState({
        projects,
        selectedProject: projects?.[0],
      });
    }
  }, [jiraProjects.data, state.isEdit]);

  //get jira project details by project id
  useEffect(() => {
    if (
      state.isEdit ||
      !state.selectedProject?.value ||
      !state.url ||
      !state.token ||
      !state.user
    )
      return;
    dispatch(
      actions.getJiraProjectDetails({
        q: {
          jiraClientRequest: {
            user: state.user,
            url: state.url,
            token: state.token,
            project_id: state.selectedProject?.value,
            include_details: true,
          },
        },
        onSuccess: res => {
          const details = res?.projects?.[0];
          const users = map(details?.users as Record<string, any>[], o => ({
            value: o?.accountId,
            label: o?.displayName,
          }));
          const boardOptions = map(details?.boards, o => ({
            label: o?.name,
            value: o?.id,
          }));
          const issueTypeOptions = map(details?.issue_types, o => ({
            label: o,
            value: o,
          }));
          updateState({
            users,
            issueTypeOptions,
            boardOptions,
          });
        },
      }),
    );
  }, [
    state.user,
    state.url,
    state.token,
    state.selectedProject,
    dispatch,
    state.isEdit,
  ]);

  //get jira project info in edit mode and set a jira project
  useEffect(() => {
    if (state.isEdit && !!currentRecord.uuid) {
      dispatch(
        actions.getJiraProjectDetailsForAClient({
          q: { clientId: currentRecord.uuid },
          onSuccess: res => {
            if (!res?.projects?.[0]) return;
            const r: JiraClientInfo = res?.projects?.[0];
            updateState({
              selectedProject: {
                label: r?.name,
                value: r?.id,
                data: r,
              },
            });
          },
        }),
      );
    }
  }, [dispatch, currentRecord, state.isEdit]);

  //set list of users, issues types and boards for selected project
  useEffect(() => {
    if (!!state.selectedProject) {
    }
  }, [state.selectedProject]);

  useEffect(() => {
    if (!state.isEdit)
      updateState({
        selectedUser: state.users?.[0],
      });
  }, [state.users, state.isEdit]);

  useEffect(() => {
    if (!state.isEdit)
      updateState({
        issueType: state.issueTypeOptions?.[0],
      });
  }, [state.issueTypeOptions, state.isEdit]);

  useEffect(() => {
    if (!state.isEdit)
      updateState({
        selectedBoard: state.boardOptions?.[0],
      });
  }, [state.boardOptions, state.isEdit]);

  useEffect(() => {
    if (state.isEdit) {
      let user: Record<string, any> = {};
      if (!!currentRecord.jira?.default_assignee_name)
        user = {
          label: currentRecord.jira?.default_assignee_name,
          value: currentRecord.jira?.account_id,
        };
      else
        user = find(
          state.users,
          o => o?.value === currentRecord.jira?.account_id,
        );
      updateState({
        selectedUser: user,
      });
    }
  }, [state.isEdit, state.users, currentRecord]);

  useEffect(() => {
    if (state.isEdit)
      updateState({
        selectedBoard: {
          label: currentRecord.jira?.default_board_name,
          value: currentRecord.jira?.default_board_id,
        },
        issueType: {
          label: currentRecord?.jira?.default_issue_type,
          value: currentRecord?.jira?.default_issue_type,
        },
      });
  }, [state.isEdit, currentRecord]);

  const schemaForm = yup.object().shape({
    name: yup
      .string()
      .matches(
        /^[a-zA-Z0-9-_ ]*$/i,
        'Only alphanumeric, hyphen, underscore and space is allowed!',
      ),
    url: yup.string().url('Url is not valid!'),
    user: yup.string().email('Email is not valid!'),
  });

  const validateJiraClient = data => {
    const {
      name,
      user,
      url,
      token,
      project_id,
      default_assignee,
      issue_type,
      board,
    } = data;
    dispatch(
      actions.validateClient({
        q: {
          clientConfig: {
            client_name: Clients.Jira,
            name,
            status: true,
            jira: {
              user,
              url,
              token,
              project_id: project_id.value,
              project_name: project_id.label,
              account_id: default_assignee?.value,
              default_assignee_name: default_assignee?.label,
              default_issue_type: issue_type?.value,
              default_board_id: board.value,
              default_board_name: board.label,
            },
          },
        },
        onSuccess: () =>
          updateState({ validateClient: true, showValidatedMessage: true }),
        onError: () =>
          updateState({ validateClient: false, showValidatedMessage: true }),
      }),
    );
  };

  const restFields: FormSchema =
    state.validateProjects || state.isEdit
      ? {
          project_id: {
            type: 'react-select',
            label: 'Default Project',
            isRequired: true,
            value: state.selectedProject,
            onChange: v =>
              updateState({
                selectedProject: v,
                validateClient: false,
                showValidatedMessage: false,
              }),
            options: state.projects,
            isLoading: jiraProjects.isLoading,
            isDisabled:
              jiraProjects.isLoading || client.isLoading || state.isEdit,
          },
          board: {
            type: 'react-select',
            label: 'Default Board',
            value: state.selectedBoard,
            onChange: v =>
              updateState({
                selectedBoard: v,
                validateClient: state.isEdit,
                showValidatedMessage: false,
              }),
            options: state.boardOptions,
            isLoading: jiraProjectDetails.isLoading,
            isDisabled: jiraProjectDetails.isLoading || client.isLoading,
            helpLabel: 'What’s this?',
            onClickHelpIcon: () => setLearnMore('jira_project_default_board'),
          },
          default_assignee: {
            type: 'react-select',
            isRequired: true,
            label: 'Default Assignee',
            value: state.selectedUser,
            onChange: v =>
              updateState({
                selectedUser: v,
                validateClient: state.isEdit,
                showValidatedMessage: false,
              }),
            options: state.users,
            isLoading: jiraProjectDetails.isLoading,
            isDisabled: jiraProjectDetails.isLoading || client.isLoading,
          },
          issue_type: {
            type: 'react-select',
            isRequired: true,
            label: 'Default Issue Type',
            value: state.issueType,
            onChange: v =>
              updateState({ issueType: v, validateClient: state.isEdit }),
            options: state.issueTypeOptions,
            isLoading: jiraProjectDetails.isLoading,
            isDisabled: jiraProjectDetails.isLoading || client.isLoading,
          },
        }
      : {};

  const token: FormSchema = state.isEdit
    ? {}
    : {
        token: {
          htmlInputType: 'password',
          passwordShow: true,
          type: 'text',
          label: 'API Token',
          isRequired: true,
          onChange: v =>
            updateState({
              token: v,
              validateToken: !!v,
              validateProjects: false,
              showValidatedMessage: false,
            }),
          helpLabel: 'How do I get this?',
          onClickHelpIcon: () => updateState({ isHelpOpen: true }),
        },
      };

  return (
    <>
      <Form
        formValidation={schemaForm}
        isLoading={activeClient.isLoading}
        schema={{
          name: {
            type: 'text',
            label: 'Connector Name',
            isRequired: true,
            value: state?.name,
            onChange: value => {
              const flag = includes(state.names, value);
              let err = '';
              if (!value.match(/^[a-zA-Z0-9-]*$/i))
                err = 'Only Alphanumeric, hyphen is allowed!';
              else if (flag) err = 'Name already exists';
              updateState({
                name: value,
                nameError: err,
                showValidatedMessage: false,
              });
            },
            error: state.nameError,
            tooltip:
              'Only alphanumeric, hyphen, underscore and space is allowed',
            isDisabled: jiraProjectDetails.isLoading || client.isLoading,
            autoFocus: true,
          },
          url: {
            type: 'text',
            label: 'URL',
            placeholder: 'https://example.atlassian.net/',
            isRequired: true,
            value: state?.url,
            onChange: v =>
              updateState({
                url: v,
                validateProjects: false,
                showValidatedMessage: false,
              }),
            isDisabled: state.isEdit,
            tooltip: 'Enter valid url',
          },
          user: {
            type: 'text',
            label: 'User ID',
            placeholder: 'example@mail.com',
            isRequired: true,
            value: state?.user,
            onChange: v =>
              updateState({
                user: v,
                validateProjects: false,
                showValidatedMessage: false,
              }),
            isDisabled: state.isEdit,
            tooltip: 'Enter valid email',
          },
          ...token,
          ...restFields,
          ...(state.showValidatedMessage && !state.isEdit
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => {
                    const err = validateClient.error?.title;
                    const success = !err;
                    return (
                      <Box w="full">
                        <Alert
                          status={success ? 'success' : 'error'}
                          borderRadius={6}
                          title={success ? `` : validateClient.error?.title}
                        >
                          <AlertIcon />
                          {success
                            ? `All fields are valid. Please click on "Add Client" to proceed.`
                            : validateClient.error?.description}
                        </Alert>
                      </Box>
                    );
                  },
                },
              }
            : {}),
        }}
        helpDrawer={{
          isOpen: state.isHelpOpen,
          onClose: () => updateState({ isHelpOpen: false }),
          content: <JIRAHelp />,
          header: <PageHeaderWithIcon label="Create an API token" />,
        }}
        buttonOptions={{
          submit: {
            isVisible: true,
            isLoading:
              jiraProjectDetails.isLoading ||
              jiraProjects.isLoading ||
              client.isLoading ||
              activeClient.isLoading ||
              validateClient.isLoading,
            name: state.isEdit
              ? 'UPDATE CLIENT'
              : !state.validateProjects
              ? 'VALIDATE & GET INFO'
              : !state.validateClient
              ? 'VALIDATE'
              : 'ADD CLIENT',
            isDisabled:
              (state.validateProjects &&
                !state.isEdit &&
                (!state.issueType ||
                  !state.projects.length ||
                  !state.users.length)) ||
              !state.validateToken ||
              !state.name ||
              !state.user ||
              !state.url,
          },
          reset: {
            isVisible: true,
            isDisabled:
              jiraProjects.isLoading ||
              jiraProjectDetails.isLoading ||
              client.isLoading,
            onClick: () => {
              onCancel();
            },
          },
        }}
        showHelp
        styles={formStyles}
        handleSubmit={data =>
          !state.validateProjects
            ? getJiraProjects(data.user, data.url, data.token)
            : !state.validateClient
            ? validateJiraClient(data)
            : handleSubmit(data)
        }
      />
      <LearnMore />
    </>
  );
});
